import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  getCustomerTemplateImportComparison,
  getCustomerTemplateImport,
  migrateCustomerTemplateTenant,
  reconcileCustomerTemplateTenantMigrations,
  refreshCustomerTemplateModules,
  reprocessCustomerTemplateImport,
  reprocessCustomerTemplateModules,
  validateCustomerTemplateTenantMigrations,
  unlockCustomerTemplateImport,
} from '../../actions/ActionCustomer';
import {
  tCustomerTemplate,
  tCustomerTemplateModule,
  tCustomerTemplateReason,
  tCustomerTemplateStage,
  tCustomerTemplateStatus,
} from '../../i18n/customerTemplateImport';
import oClasses from './CustomerIndex.module.scss';

const EMPTY_VALUE = '-';
const AUDIT_MODULE_ORDER = ['system', 'documents', 'norm', 'operational_panel', 'ciq', 'indicator'];

/**
 * Exibe auditoria da aplicacao de modelo e permite reprocessamento controlado.
 */
const CustomerTemplateImportModal = ({ nCustId, setAlert = () => {} }) => {
  const [oImport, setImport] = useState(null);
  const [bIsLoading, setIsLoading] = useState(true);
  const [bIsReprocessing, setIsReprocessing] = useState(false);
  const [bIsModuleReprocessing, setIsModuleReprocessing] = useState(false);
  const [bIsRefreshingModel, setIsRefreshingModel] = useState(false);
  const [bIsUnlocking, setIsUnlocking] = useState(false);
  const [bIsMigratingTenant, setIsMigratingTenant] = useState(false);
  const [bIsAuditingTenant, setIsAuditingTenant] = useState(false);
  const [bIsReconcilingTenant, setIsReconcilingTenant] = useState(false);
  const [bIsComparing, setIsComparing] = useState(false);
  const [cSelectedModule, setSelectedModule] = useState('');
  const [oComparison, setComparison] = useState(null);

  /**
   * Busca a ultima execucao registrada para o cliente selecionado.
   */
  const loadImport = useCallback(async () => {
    try {
      setIsLoading(true);
      const oResponse = await getCustomerTemplateImport(nCustId);
      setImport(oResponse);
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.loadError')]);
    } finally {
      setIsLoading(false);
    }
  }, [nCustId, setAlert]);

  /**
   * Agenda reprocessamento pendente ou falho mantendo a auditoria atualizada.
   */
  const reprocessImport = async () => {
    if (!oImport || !oImport.id) return;

    try {
      setIsReprocessing(true);
      await reprocessCustomerTemplateImport(oImport.id);
      setAlert('success', tCustomerTemplate('customerTemplate.reprocessSuccess'));
      await loadImport();
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.reprocessError')]);
    } finally {
      setIsReprocessing(false);
    }
  };

  /**
   * Atualiza o schema do cliente sem repetir a copia de dados do modelo.
   */
  const migrateTenant = async () => {
    if (!oImport || !oImport.id) return;

    try {
      setIsMigratingTenant(true);
      await migrateCustomerTemplateTenant(oImport.id);
      setAlert('success', tCustomerTemplate('customerTemplate.migrateTenantSuccess'));
      await loadImport();
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.migrateTenantError')]);
    } finally {
      setIsMigratingTenant(false);
    }
  };

  const validateTenantMigrations = async () => {
    try { setIsAuditingTenant(true); await validateCustomerTemplateTenantMigrations(oImport.id); setAlert('success', tCustomerTemplate('customerTemplate.auditTenantSuccess')); await loadImport(); }
    catch (oError) { setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.migrateTenantError')]); }
    finally { setIsAuditingTenant(false); }
  };

  const reconcileTenantMigrations = async () => {
    const aMigrations = ((oImport.tenant_migration_audit || {}).items || []).filter(oItem => oItem.action === 'reconcile').map(oItem => oItem.migration);
    if (!aMigrations.length) return;
    try { setIsReconcilingTenant(true); await reconcileCustomerTemplateTenantMigrations(oImport.id, aMigrations); await loadImport(); }
    catch (oError) { setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.migrateTenantError')]); }
    finally { setIsReconcilingTenant(false); }
  };

  /**
   * Marca uma execucao sem progresso como falha para liberar reprocessamento.
   */
  const unlockImport = async () => {
    if (!oImport || !oImport.id) return;

    try {
      setIsUnlocking(true);
      await unlockCustomerTemplateImport(oImport.id);
      setAlert('success', tCustomerTemplate('customerTemplate.unlockSuccess'));
      await loadImport();
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.unlockError')]);
    } finally {
      setIsUnlocking(false);
    }
  };

  /**
   * Rele o plano e a estrutura existente do cliente origem sem iniciar uma importacao.
   */
  const refreshModelModules = async () => {
    if (!oImport || !oImport.template_id) return;

    try {
      setIsRefreshingModel(true);
      const oResponse = await refreshCustomerTemplateModules(oImport.template_id);
      setImport({
        ...oImport,
        modules_config: oResponse.modules_config,
      });
      setAlert('success', tCustomerTemplate('customerTemplate.refreshModelSuccess'));
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.refreshModelError')]);
    } finally {
      setIsRefreshingModel(false);
    }
  };

  /**
   * Agenda um modulo que ja existe no inventario do modelo, sem apagar resultados dos demais.
   */
  const reprocessModule = async () => {
    if (!oImport || !oImport.id || !cSelectedModule) return;

    try {
      setIsModuleReprocessing(true);
      await reprocessCustomerTemplateModules(oImport.id, [cSelectedModule]);
      setAlert('success', tCustomerTemplate('customerTemplate.reprocessModuleSuccess'));
      await loadImport();
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.reprocessModuleError')]);
    } finally {
      setIsModuleReprocessing(false);
    }
  };

  /**
   * Consulta sob demanda o comparativo dos IDs confirmados na importacao.
   */
  const loadComparison = async () => {
    if (!oImport || !oImport.id) return;

    try {
      setIsComparing(true);
      setComparison(await getCustomerTemplateImportComparison(oImport.id));
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || [tCustomerTemplate('customerTemplate.comparisonError')]);
    } finally {
      setIsComparing(false);
    }
  };

  useEffect(() => {
    loadImport();
  }, [loadImport]);

  if (bIsLoading) {
    return <p>{tCustomerTemplate('customerTemplate.loading')}</p>;
  }

  if (!oImport) {
    return <p>{tCustomerTemplate('customerTemplate.empty')}</p>;
  }

  const bCanReprocessBase = !!oImport.can_reprocess || ['failed', 'partially_completed'].includes(oImport.status);
  const bCanReprocessModule = ['completed', 'failed', 'partially_completed'].includes(oImport.status);
  const bCanUnlock = !!oImport.can_unlock_running;
  const oTenantMigration = oImport.tenant_migration || {};
  const oTenantAudit = oImport.tenant_migration_audit || {};
  const aAuditItems = oTenantAudit.items || [];
  const aReconcileItems = (oTenantAudit.items || []).filter(oItem => oItem.action === 'reconcile');
  const bCanMigrateTenant = !!oTenantMigration.can_run;
  const bIsBusy = bIsLoading || bIsReprocessing || bIsModuleReprocessing || bIsRefreshingModel || bIsUnlocking || bIsMigratingTenant || bIsAuditingTenant || bIsReconcilingTenant || bIsComparing;
  const aSkippedAttachments = getSkippedAttachments(oImport.id_map);
  const aEnabledModules = getEnabledModules(oImport.modules_config);

  return (
    <div className={oClasses.templateImportModal}>
      <dl className={oClasses.templateImportDetails}>
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.template')} cValue={oImport.template_name} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.status')} cValue={tCustomerTemplateStatus(oImport.status)} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.sourceCustomer')} cValue={oImport.source_cust_nid} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.targetCustomer')} cValue={oImport.target_cust_nid} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.startedAt')} cValue={formatDate(oImport.started_at)} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.finishedAt')} cValue={formatDate(oImport.finished_at)} />
        <DetailItem cLabel={tCustomerTemplate('customerTemplate.updatedAt')} cValue={formatDate(oImport.updated_at)} />
      </dl>

      {oImport.status === 'running' && (
        <p className={oClasses.templateImportNotice}>
          {oImport.is_running_stale
            ? tCustomerTemplate('customerTemplate.staleRunning')
            : tCustomerTemplate('customerTemplate.runningHint')}
        </p>
      )}

      {!!oImport.error_message && (
        <section className={oClasses.templateImportSection}>
          <h3>{tCustomerTemplate('customerTemplate.error')}</h3>
          <pre>{oImport.error_message}</pre>
        </section>
      )}

      <section className={oClasses.templateImportSection}>
        <div className={oClasses.templateImportSectionHeader}>
          <h3>{tCustomerTemplate('customerTemplate.tenantMigration')}</h3>
          <p className={oClasses.templateImportHint}>{tCustomerTemplate('customerTemplate.auditHint')}</p>
        </div>
        {!!oTenantAudit.status && (
          <p className={oClasses.templateImportAuditStatus}>
            {`${tCustomerTemplate('customerTemplate.status')}: ${tCustomerTemplateStatus(oTenantAudit.status)}`}
          </p>
        )}
        {aAuditItems.length > 0 && (
          <ul className={oClasses.templateImportAuditList}>
            {aAuditItems.map(oItem => <li key={oItem.migration}>{`${oItem.migration} — ${oItem.action}: ${oItem.reason}`}</li>)}
          </ul>
        )}
        <dl className={oClasses.templateImportDetails}>
          <DetailItem
            cLabel={tCustomerTemplate('customerTemplate.tenantMigrationStatus')}
            cValue={oTenantMigration.status ? tCustomerTemplateStatus(oTenantMigration.status) : EMPTY_VALUE}
          />
          <DetailItem
            cLabel={tCustomerTemplate('customerTemplate.tenantMigrationStartedAt')}
            cValue={formatDate(oTenantMigration.started_at)}
          />
          <DetailItem
            cLabel={tCustomerTemplate('customerTemplate.tenantMigrationFinishedAt')}
            cValue={formatDate(oTenantMigration.finished_at)}
          />
        </dl>
        {!!oTenantMigration.error_message && <pre>{oTenantMigration.error_message}</pre>}
      </section>

      <section className={oClasses.templateImportSection}>
        <h3>{tCustomerTemplate('customerTemplate.modules')}</h3>
        <table>
          <tbody>
            {Object.entries(oImport.modules_status || {}).map(([cModule, mStatus]) => (
              <tr key={cModule}>
                <td>{tCustomerTemplateModule(cModule)}</td>
                <td>{formatModuleStatus(mStatus)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <ImportComparison
        oComparison={oComparison}
        bIsLoading={bIsComparing}
        onLoad={loadComparison}
      />

      {aSkippedAttachments.length > 0 && (
        <section className={oClasses.templateImportSection}>
          <h3>{tCustomerTemplate('customerTemplate.missingFiles')}</h3>
          <div className={oClasses.tableScroll}>
            <table className={oClasses.missingFilesTable}>
              <thead>
                <tr>
                  <th>{tCustomerTemplate('customerTemplate.attachmentId')}</th>
                  <th>{tCustomerTemplate('customerTemplate.versionId')}</th>
                  <th>{tCustomerTemplate('customerTemplate.reason')}</th>
                  <th>{tCustomerTemplate('customerTemplate.path')}</th>
                </tr>
              </thead>
              <tbody>
                {aSkippedAttachments.map(oAttachment => (
                  <tr key={oAttachment.id}>
                    <td>{oAttachment.id}</td>
                    <td>{oAttachment.source_version_id || EMPTY_VALUE}</td>
                    <td>{tCustomerTemplateReason(oAttachment.reason)}</td>
                    <td className={oClasses.filePathCell}>{oAttachment.source_path || EMPTY_VALUE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!!oImport.id_map && <IdMapAudit oIdMap={oImport.id_map} />}

      <div className={oClasses.templateImportActions}>
        <div className={oClasses.templateImportStatusActions}>
          <button
            type="button"
            className="btn btn btn-secondary"
            onClick={loadImport}
            disabled={bIsBusy}
          >
            {tCustomerTemplate('customerTemplate.refresh')}
          </button>

          {bCanUnlock && (
            <button
              type="button"
              className="btn btn btn-secondary"
              onClick={unlockImport}
              disabled={bIsBusy}
            >
              {bIsUnlocking
                ? tCustomerTemplate('customerTemplate.unlocking')
                : tCustomerTemplate('customerTemplate.unlock')}
            </button>
          )}

          {bCanReprocessBase && (
            <button
              type="button"
              className="btn btn btn-secondary"
              onClick={reprocessImport}
              disabled={bIsBusy}
            >
              {bIsReprocessing
                ? tCustomerTemplate('customerTemplate.reprocessing')
                : tCustomerTemplate('customerTemplate.reprocess')}
            </button>
          )}

          {bCanMigrateTenant && (
            <button
              type="button"
              className="btn btn btn-secondary"
              onClick={migrateTenant}
              disabled={bIsBusy}
            >
              {bIsMigratingTenant
                ? tCustomerTemplate('customerTemplate.migratingTenant')
                : tCustomerTemplate('customerTemplate.migrateTenant')}
            </button>
          )}

          {!!oTenantAudit.can_validate && <button type="button" className="btn btn btn-secondary" onClick={validateTenantMigrations} disabled={bIsBusy}>{bIsAuditingTenant ? tCustomerTemplate('customerTemplate.auditingTenant') : tCustomerTemplate('customerTemplate.auditTenant')}</button>}
          {aReconcileItems.length > 0 && oTenantAudit.status === 'completed' && <button type="button" className="btn btn btn-secondary" onClick={reconcileTenantMigrations} disabled={bIsBusy}>{bIsReconcilingTenant ? tCustomerTemplate('customerTemplate.reconcilingTenant') : tCustomerTemplate('customerTemplate.reconcileTenant')}</button>}
        </div>

        {bCanReprocessModule && !!oImport.template_id && (
          <div className={oClasses.templateImportRecoveryActions}>
            <button
              type="button"
              className="btn btn btn-secondary"
              onClick={refreshModelModules}
              disabled={bIsBusy}
            >
              {bIsRefreshingModel
                ? tCustomerTemplate('customerTemplate.refreshingModel')
                : tCustomerTemplate('customerTemplate.refreshModel')}
            </button>

            {aEnabledModules.length > 0 && (
              <div className={oClasses.templateImportModuleRecovery}>
                <select
                  value={cSelectedModule}
                  onChange={oEvent => setSelectedModule(oEvent.target.value)}
                  disabled={bIsBusy}
                >
                  <option value="">{tCustomerTemplate('customerTemplate.selectModule')}</option>
                  {aEnabledModules.map(cModule => (
                    <option key={cModule} value={cModule}>
                      {tCustomerTemplateModule(cModule)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn btn-secondary"
                  onClick={reprocessModule}
                  disabled={bIsBusy || !cSelectedModule}
                >
                  {bIsModuleReprocessing
                    ? tCustomerTemplate('customerTemplate.reprocessing')
                    : tCustomerTemplate('customerTemplate.reprocessModule')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * Converte o mapa de anexos ignorados em lista renderizavel na auditoria.
 */
const getSkippedAttachments = oIdMap => (
  Object.entries((oIdMap && oIdMap.skipped_attachments) || {}).map(([cAttachmentId, oAttachment]) => ({
    id: cAttachmentId,
    ...oAttachment,
  }))
);

/**
 * Filtra o inventario retornado pelo backend para impedir o envio de modulos desabilitados.
 */
const getEnabledModules = oModulesConfig => (
  Object.keys(oModulesConfig || {}).filter(cModule => {
    const mConfig = oModulesConfig[cModule];
    return typeof mConfig === 'boolean' ? mConfig : !!(mConfig && mConfig.enabled);
  })
);

/**
 * Exibe status simples e progresso salvo por modulo durante importacoes longas.
 */
const formatModuleStatus = (mStatus) => {
  if (!mStatus || typeof mStatus === 'string') {
    return tCustomerTemplateStatus(mStatus);
  }

  const cStatus = mStatus.status || EMPTY_VALUE;
  const oDetail = mStatus.detail || null;
  const cStatusLabel = tCustomerTemplateStatus(cStatus);
  const cError = mStatus.error_message || mStatus.error || '';

  if (!oDetail || !oDetail.stage) {
    return formatModuleStatusLabel(cStatusLabel, '', cError);
  }

  const cStage = tCustomerTemplateStage(oDetail.stage);
  const cProgress = oDetail.total
    ? tCustomerTemplate('customerTemplate.statusDetail.count', {
      stage: cStage,
      processed: oDetail.processed || 0,
      total: oDetail.total,
    })
    : tCustomerTemplate('customerTemplate.statusDetail.stage', {
      stage: cStage,
    });

  return formatModuleStatusLabel(cStatusLabel, cProgress, cError);
};

/**
 * Monta status, progresso e erro do modulo em linhas separadas.
 */
const formatModuleStatusLabel = (cStatusLabel, cProgress, cError) => (
  <span className={oClasses.moduleStatus}>
    <span>{`${cStatusLabel}${cProgress ? ` ${cProgress}` : ''}`}</span>
    {!!cError && (
      <small>
        {tCustomerTemplate('customerTemplate.statusDetail.error', {
          message: cError,
        })}
      </small>
    )}
  </span>
);

/**
 * Mostra um recorte somente-leitura da importacao, sem confundir dados
 * proprios do cliente com registros que vieram da base-modelo.
 */
const ImportComparison = ({ oComparison, bIsLoading, onLoad }) => {
  const aModules = (oComparison && oComparison.modules) || [];

  return (
    <section className={oClasses.templateImportSection}>
      <details className={oClasses.comparisonDetails}>
        <summary>{tCustomerTemplate('customerTemplate.comparison')}</summary>
        <p className={oClasses.templateImportHint}>{tCustomerTemplate('customerTemplate.comparisonHint')}</p>
        <div className={oClasses.comparisonActions}>
          <button type="button" className="btn btn-secondary" onClick={onLoad} disabled={bIsLoading}>
            {bIsLoading
              ? tCustomerTemplate('customerTemplate.generatingComparison')
              : tCustomerTemplate(oComparison ? 'customerTemplate.refreshComparison' : 'customerTemplate.generateComparison')}
          </button>
          {!!oComparison && (
            <span>{tCustomerTemplate('customerTemplate.comparisonGeneratedAt', { date: formatDate(oComparison.generated_at) })}</span>
          )}
        </div>

        {!!aModules.length && (
          <div className={oClasses.tableScroll}>
            <table className={oClasses.comparisonTable}>
              <thead>
                <tr>
                  <th>{tCustomerTemplate('customerTemplate.modules')}</th>
                  <th>{tCustomerTemplate('customerTemplate.comparisonModel')}</th>
                  <th>{tCustomerTemplate('customerTemplate.comparisonTarget')}</th>
                  <th>{tCustomerTemplate('customerTemplate.comparisonAttention')}</th>
                  <th>{tCustomerTemplate('customerTemplate.status')}</th>
                </tr>
              </thead>
              <tbody>
                {aModules.map(oModule => oModule.groups.map(oGroup => (
                  <tr key={`${oModule.key}-${oGroup.key}`}>
                    <td>{`${tCustomerTemplateModule(oModule.key)} — ${tCustomerTemplateStage(oGroup.key)}`}</td>
                    <td>{oGroup.source_total}</td>
                    <td>{oGroup.confirmed_total}</td>
                    <td>{oGroup.attention_total}</td>
                    <td>{tCustomerTemplate(`customerTemplate.comparisonStatus.${oGroup.status}`)}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </section>
  );
};

ImportComparison.propTypes = {
  oComparison: PropTypes.shape({
    generated_at: PropTypes.string,
    modules: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      groups: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        source_total: PropTypes.number,
        confirmed_total: PropTypes.number,
        attention_total: PropTypes.number,
        status: PropTypes.string,
      })),
    })),
  }),
  bIsLoading: PropTypes.bool.isRequired,
  onLoad: PropTypes.func.isRequired,
};

/**
 * Exibe o mapa tecnico em uma visao de auditoria agrupada por modulo.
 */
const IdMapAudit = ({ oIdMap }) => {
  const aModules = getAuditModules(oIdMap);
  const [cActiveModule, setActiveModule] = useState((aModules[0] && aModules[0].key) || '');
  const oActiveModule = aModules.find(oModule => oModule.key === cActiveModule) || aModules[0];

  if (!aModules.length || !oActiveModule) {
    return (
      <section className={oClasses.templateImportSection}>
        <h3>{tCustomerTemplate('customerTemplate.idMap')}</h3>
        <pre>{JSON.stringify(oIdMap, null, 2)}</pre>
      </section>
    );
  }

  return (
    <section className={oClasses.templateImportSection}>
      <div className={oClasses.idMapHeader}>
        <h3>{tCustomerTemplate('customerTemplate.idMap')}</h3>
        <span>{tCustomerTemplate('customerTemplate.idMapAuditHint')}</span>
      </div>

      <div className={oClasses.idMapTabs}>
        {aModules.map(oModule => (
          <button
            key={oModule.key}
            type="button"
            className={oModule.key === oActiveModule.key ? oClasses.idMapTabActive : undefined}
            onClick={() => setActiveModule(oModule.key)}
          >
            {tCustomerTemplateModule(oModule.key)}
            <span>{oModule.total}</span>
          </button>
        ))}
      </div>

      <div className={oClasses.idMapSummaryGrid}>
        {oActiveModule.groups.map(oGroup => (
          <div key={oGroup.key} className={oClasses.idMapSummaryCard}>
            <span>{tCustomerTemplateStage(oGroup.key)}</span>
            <strong>{oGroup.count}</strong>
          </div>
        ))}
      </div>

      <div className={oClasses.idMapGroups}>
        {oActiveModule.groups.map(oGroup => (
          <IdMapGroup key={oGroup.key} oGroup={oGroup} />
        ))}
      </div>

      <details className={oClasses.idMapRawDetails}>
        <summary>{tCustomerTemplate('customerTemplate.idMapRaw')}</summary>
        <pre>{JSON.stringify(oIdMap, null, 2)}</pre>
      </details>
    </section>
  );
};

/**
 * Renderiza um grupo do mapa de IDs com detalhe recolhivel para manter a modal compacta.
 */
const IdMapGroup = ({ oGroup }) => {
  const bHasNames = oGroup.items.some(oItem => !!oItem.name);

  return (
    <details className={oClasses.idMapGroup}>
      <summary>
        <span>{tCustomerTemplateStage(oGroup.key)}</span>
        <strong>{oGroup.count}</strong>
      </summary>
      <div className={oClasses.tableScroll}>
        <table className={oClasses.idMapTable}>
          <thead>
            <tr>
              {bHasNames && <th>{tCustomerTemplate('customerTemplate.idMapName')}</th>}
              <th>{tCustomerTemplate('customerTemplate.idMapSourceId')}</th>
              <th>{tCustomerTemplate('customerTemplate.idMapTargetId')}</th>
            </tr>
          </thead>
          <tbody>
            {oGroup.items.map(oItem => (
              <tr key={`${oGroup.key}-${oItem.source_id}-${oItem.target_id}`}>
                {bHasNames && <td>{oItem.name || EMPTY_VALUE}</td>}
                <td>{oItem.source_id}</td>
                <td>{oItem.target_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
};

/**
 * Normaliza o _audit novo e mantem fallback para mapas antigos ja gravados.
 */
const getAuditModules = (oIdMap) => {
  const oAudit = (oIdMap && oIdMap._audit) || {};

  return AUDIT_MODULE_ORDER.map((cModule) => {
    const aAuditModule = oAudit[cModule] || {};
    const aGroups = (aAuditModule.groups || getFallbackAuditGroups(oIdMap, cModule))
      .filter(oGroup => oGroup && oGroup.count > 0)
      .map(oGroup => ({
        ...oGroup,
        items: (oGroup.items || []).slice(0, 200),
      }));

    return {
      key: cModule,
      groups: aGroups,
      total: aGroups.reduce((nTotal, oGroup) => nTotal + oGroup.count, 0),
    };
  }).filter(oModule => oModule.total > 0);
};

/**
 * Cria uma auditoria simples para importacoes antigas que ainda nao possuem _audit.
 */
const getFallbackAuditGroups = (oIdMap, cModule) => {
  const aGroupKeys = {
    system: ['permission_groups', 'teams'],
    documents: ['folders', 'documents', 'versions', 'attachments'],
    norm: ['norms', 'requisites', 'requisite_sections', 'item_checklists'],
    operational_panel: ['op_panel_fields', 'op_panel_field_options', 'operational_panels', 'operational_panel_boards', 'op_panel_blocks', 'op_panel_model_boards', 'op_panel_model_board_fields'],
    ciq: ['ciq_sectors', 'ciq_result_options', 'ciq_westgard_rules', 'ciq_causes', 'ciq_analytes', 'ciq_controls', 'ciq_equipments'],
    indicator: ['indicator_tabs', 'indicators'],
  }[cModule] || [];

  return aGroupKeys.map((cMapKey) => {
    const oMap = (oIdMap && oIdMap[cMapKey]) || {};
    const aItems = Object.entries(oMap).map(([cSourceId, nTargetId]) => ({
      source_id: Number(cSourceId),
      target_id: Number(nTargetId),
      name: null,
    }));

    return {
      key: mapKeyToStage(cMapKey),
      map_key: cMapKey,
      count: aItems.length,
      items: aItems,
    };
  });
};

/**
 * Converte chaves tecnicas antigas para as mesmas etapas traduzidas do progresso.
 */
const mapKeyToStage = cMapKey => ({
  op_panel_fields: 'fields',
  op_panel_field_options: 'field_options',
  operational_panels: 'panels',
  operational_panel_boards: 'boards',
  op_panel_blocks: 'blocks',
  op_panel_model_boards: 'model_boards',
  op_panel_model_board_fields: 'model_board_fields',
  ciq_sectors: 'sectors',
  ciq_result_options: 'result_options',
  ciq_westgard_rules: 'westgard_rules',
  ciq_causes: 'causes',
  ciq_analytes: 'analytes',
  ciq_controls: 'controls',
  ciq_equipments: 'equipments',
  indicator_tabs: 'tabs',
}[cMapKey] || cMapKey);

/**
 * Renderiza um par label/valor mantendo fallback consistente.
 */
const DetailItem = ({ cLabel, cValue }) => (
  <>
    <dt>{cLabel}</dt>
    <dd>{cValue || EMPTY_VALUE}</dd>
  </>
);

/**
 * Formata datas vindas do backend sem alterar o fuso informado pela API.
 */
const formatDate = cDate => (
  cDate ? moment.parseZone(cDate).format('DD/MM/YYYY H:mm') : EMPTY_VALUE
);

CustomerTemplateImportModal.propTypes = {
  nCustId: PropTypes.number.isRequired,
  setAlert: PropTypes.func,
};

DetailItem.propTypes = {
  cLabel: PropTypes.string.isRequired,
  cValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

IdMapAudit.propTypes = {
  oIdMap: PropTypes.object.isRequired,
};

IdMapGroup.propTypes = {
  oGroup: PropTypes.shape({
    count: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      source_id: PropTypes.number.isRequired,
      target_id: PropTypes.number.isRequired,
    })).isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
};

DetailItem.defaultProps = {
  cValue: EMPTY_VALUE,
};

export default CustomerTemplateImportModal;
